import React, { useEffect, useMemo } from 'react';
import { Controller, ControllerRenderProps, FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { REQUIRED_TEXT_ERROR } from '@components/dynamic-input-fields/src/utils';
import { ErrorMessage } from '@components/error/src/error-message';
import { LabelPrimary } from '@components/label';
import { Multiselect } from '@components/multiselect/multiselect';
import { Select, SelectOption } from '@components/select';
import { SINGLE_CHOICE_ADDON } from '@constants/admissions';
import { Addon, AddonType } from '@domain/addon';
import { formatNumber } from '@utils/format-number/format-number';
import { getDefaultAddonNameTranslation } from '@utils/get-default-addon-name-translation/get-default-addon-name-translation';
import { getTranslatedPropertyBasedOnOrder } from '@utils/get-translated-property-based-on-order/get-translated-property-based-on-order';

import { InputSelectAddonsProps } from './input-select-addons.type';

export const InputSelectAddons = ({
  addOnsList,
  required,
  mode,
  selectedAddons,
  setSelectedAddons,
  parentRef,
}: InputSelectAddonsProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const addonName = (addon: Addon, type: AddonType) =>
    `${
      getTranslatedPropertyBasedOnOrder({
        translations: addon.name,
        preferredLanguage: locale,
      }) ?? t(getDefaultAddonNameTranslation(type))
    } - ${formatNumber({ number: addon.fixedPrice.amount, locale, currency: addon.fixedPrice.currency })}`;

  const multiSearchTranslations = useMemo(() => {
    return {
      deselectAll: t('offers:deselectAllLabel'),
      selectAll: t('offers:selectAllLabel'),
      noResultsFound: t('offers:admissionTicketAddonsNoResultsFound'),
      noOptionsSelected: t('offers:admissionTicketAddonsNoneSelected'),
      searchLabel: t('offers:admissionTicketAddonsSearchLabel'),
      searchPlaceholder: t('offers:admissionTicketAddonsSearchPlaceholder'),
    };
  }, [t]);

  const handleSingleAddonSelection = (
    field: ControllerRenderProps<FieldValues, 'addOns'>,
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    field.onChange(e);
  };

  const handleSelectAllAddons = (field: ControllerRenderProps<FieldValues, 'addOns'>, value: boolean) => {
    const allAddons = value
      ? selectedAddons.map((channel) => ({
          ...channel,
          checked: true,
        }))
      : selectedAddons.map((channel) => ({
          ...channel,
          checked: false,
        }));
    setSelectedAddons(allAddons);
    const selection = allAddons.filter((channel) => !!channel.checked).map((channel) => channel.id);
    field.onChange(selection);
  };

  const handleAddOnsChange = (id: string, value: boolean, field: ControllerRenderProps<FieldValues, 'addOns'>) => {
    const allAddons = selectedAddons.map((channel) => {
      if (channel.id === id) {
        return {
          ...channel,
          checked: value,
        };
      }
      return channel;
    });
    setSelectedAddons(allAddons);
    const selection = allAddons.filter((channel) => !!channel.checked).map((channel) => channel.id);
    field.onChange(selection);
  };

  const mapAddons = (addons?: Addon[] | null) => {
    if (!addons) return [];
    return addons.map((addon) => ({
      id: addon.id,
      name: addonName(addon, addon.type),
      checked: false,
    }));
  };

  const multiSelectAddonOptions = mapAddons(addOnsList);
  useEffect(() => {
    if (selectedAddons.length === 0 && multiSelectAddonOptions.length) setSelectedAddons(multiSelectAddonOptions);
  }, [multiSelectAddonOptions, selectedAddons, setSelectedAddons]);

  return (
    <fieldset className='flex flex-col'>
      <LabelPrimary htmlFor='addOns'>{t('offers:selectAnAddOn')}</LabelPrimary>
      <Controller
        name='addOns'
        defaultValue={[] as string[]}
        control={control}
        rules={{
          required: {
            message: t(REQUIRED_TEXT_ERROR),
            value: required,
          },
        }}
        render={({ field }) =>
          mode === SINGLE_CHOICE_ADDON ? (
            <Select
              {...field}
              hasError={!!errors.addOns}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleSingleAddonSelection(field, e)}>
              <SelectOption label={t('offers:selectAnAddOn')} value={''} />
              {addOnsList?.map((addon) => {
                return <SelectOption key={addon.id} value={addon.id} label={addonName(addon, addon.type)} />;
              })}
            </Select>
          ) : (
            <Multiselect
              {...field}
              ref={null}
              parentRef={parentRef}
              id='addons'
              hasError={!!errors.addOns}
              translations={multiSearchTranslations}
              onChange={(id, value) => handleAddOnsChange(id, value, field)}
              handleSelectedAll={(value) => handleSelectAllAddons(field, value)}
              options={selectedAddons}
            />
          )
        }
      />
      <ErrorMessage name='addOns' />
    </fieldset>
  );
};
